export interface CachedItemsResponse<T> {
  items: T[];
}

interface CachePayload<T> {
  timestamp: number;
  items: T[];
}

function parseJsonSafely(text: string): unknown {
  const trimmed = text.trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    // Some endpoints may return extra characters after the JSON.
    // Try to parse only the JSON substring between first { / [ and last } / ].
    const firstCurly = trimmed.indexOf('{');
    const firstSquare = trimmed.indexOf('[');
    const first =
      Math.min(firstCurly === -1 ? Infinity : firstCurly, firstSquare === -1 ? Infinity : firstSquare);
    const lastCurly = trimmed.lastIndexOf('}');
    const lastSquare = trimmed.lastIndexOf(']');
    const last = Math.max(lastCurly, lastSquare);

    if (!Number.isFinite(first) || last === -1) {
      throw new Error('Invalid JSON response');
    }

    const candidate = trimmed.slice(first, last + 1);
    return JSON.parse(candidate);
  }
}

export async function fetchItemsWithCache<T>(
  url: string,
  cacheKey: string,
  ttlMs: number
): Promise<T[]> {
  // Try cache first
  const cached = typeof window !== 'undefined' ? localStorage.getItem(cacheKey) : null;
  if (cached) {
    try {
      const parsed = parseJsonSafely(cached) as CachePayload<T>;
      const nowMs = Date.now();

      if (nowMs - parsed.timestamp < ttlMs) {
        return parsed.items;
      }
    } catch {
      // ignore invalid cache and fall through to network
    }
  }

  const response = await fetch(url);
  if (!response.ok) {
    const responseText = await response.text();
    let details = responseText;
    try {
      const body = JSON.parse(responseText) as { message?: unknown; error?: unknown };
      const message =
        typeof body.message === 'string'
          ? body.message
          : typeof body.error === 'string'
            ? body.error
            : null;
      if (message) details = message;
    } catch {
      // Preserve a plain-text gateway error when the response is not JSON.
    }

    throw new Error(
      details
        ? `Request failed (${response.status}): ${details}`
        : `Request failed (${response.status}) while loading data from ${url}`
    );
  }

  const rawText = await response.text();
  const data: CachedItemsResponse<T> | { items?: T[] } = parseJsonSafely(rawText) as
    | CachedItemsResponse<T>
    | { items?: T[] };
  const items: T[] = (data as any).items ?? [];

  if (typeof window !== 'undefined') {
    const payload: CachePayload<T> = {
      timestamp: Date.now(),
      items,
    };
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  }

  return items;
}
